/**
 * Home (spec page 1, approved):
 *  - Baker: "My day" - Working on now (today's planner), Needs triage (New),
 *    Stuck > 7 days, Waiting on others, a this-week strip.
 *  - Robin: "Company snapshot" - open requests by division as tiles, pipeline
 *    counts, his own asks, recently shipped.
 * Either editor can flip between the two; the default follows who signed in.
 */
import { useState } from 'react';
import { DIVISIONS, STAGE_GROUPS, STATUSES, score, tier, type Settings } from '../config';
import { acceptEmail, ageDays, daysInStage, dismissEmail, useEmailDrafts, type EmailDraft, type Me, type Req } from '../lib/data';
import { effectiveSlot, lineupOrder } from '../lib/order';
import { Card } from '../parts';
import { Empty, StatusTag, TierBadge } from '../ui';
import { Guide } from '../guide';

const within = (t: { toMillis(): number } | undefined, days: number) => !!t && Date.now() - t.toMillis() < days * 86400000;

export function Home({ me, items, settings, go }: { me: Me; items: Req[]; settings: Settings; go: (to: string) => void }) {
  const isRobin = /^robin@/i.test(me.email);
  const [view, setView] = useState<'day' | 'snapshot'>(() => (isRobin ? 'snapshot' : 'day'));
  const drafts = useEmailDrafts(me);
  return (
    <>
      <div className="seg">
        <button className={view === 'day' ? 'on' : ''} onClick={() => setView('day')}>My day</button>
        <button className={view === 'snapshot' ? 'on' : ''} onClick={() => setView('snapshot')}>Company snapshot</button>
      </div>
      <Guide page="home" />
      {view === 'day' ? <MyDay items={items} settings={settings} go={go} drafts={drafts} /> : <Snapshot items={items} settings={settings} go={go} />}
    </>
  );
}

function MyDay({ items, settings, go, drafts = [] }: { items: Req[]; settings: Settings; go: (to: string) => void; drafts?: EmailDraft[] }) {
  const open = items.filter((r) => r.status !== 'completed');
  // "Working on now" = today's planner + anything in Development (Baker, 2026-09-25).
  const planned = open.filter((r) => effectiveSlot(r.plannerSlot, r.plannerDate).slot === 'today');
  const inDev = open.filter((r) => ['scoping', 'building', 'internal_test'].includes(r.status));
  const today = lineupOrder([...planned, ...inDev.filter((r) => !planned.includes(r))]);
  const triage = open.filter((r) => r.status === 'new').sort((a, b) => ageDays(b.createdAt) - ageDays(a.createdAt));
  const stuck = open.filter((r) => r.status !== 'new' && daysInStage(r) > 7).sort((a, b) => daysInStage(b) - daysInStage(a));
  const waiting = open.filter((r) => r.waitingOn);
  const week = open.filter((r) => ['today', 'tomorrow', 'week'].includes(effectiveSlot(r.plannerSlot, r.plannerDate).slot));
  const plannedHours = week.reduce((s, r) => s + (r.estHours ?? 0), 0);
  const doneWeek = items.filter((r) => r.status === 'completed' && within(r.completedAt, 7)).length;
  const releasedWeek = items.filter((r) => within(r.stageTimes?.release, 7)).length;
  const todayHours = planned.reduce((s, r) => s + (r.estHours ?? 0), 0);
  const open_ = (id: string) => go(`/r/${id}`);
  const suggestions = today.length ? [] : lineupOrder(open.filter((r) => r.status !== 'new')).slice(0, 3);

  return (
    <div className="home">
      <section className="card now">
        <div className="sec-head">
          <h2>Working on now <span className="count">{today.length}</span></h2>
          <span className="muted small">today's plan + everything in Development - {todayHours}h of {settings.capacityDay}h planned</span>
          <span className="spacer" />
          <button className="secondary" onClick={() => go('/planner')}>Planner</button>
        </div>
        {today.length ? (
          <div className="card-grid">
            {today.map((r) => <Card key={r.id} r={r} settings={settings} onOpen={open_}
              extra={<>
                {planned.includes(r) ? <div className="src-tag">on today's plan</div> : <div className="src-tag">in development</div>}
                {effectiveSlot(r.plannerSlot, r.plannerDate).rolled && <div className="rolled">rolled over</div>}
              </>} />)}
          </div>
        ) : (
          <>
            <Empty>Nothing planned for today. Top of the lineup:</Empty>
            <div className="card-grid">{suggestions.map((r) => <Card key={r.id} r={r} settings={settings} onOpen={open_} />)}</div>
          </>
        )}
      </section>

      <div className="home-cols">
        <section className="card">
          <div className="sec-head"><h2>Needs triage <span className="count">{triage.length + drafts.length}</span></h2></div>
          {drafts.length > 0 && <EmailDrafts drafts={drafts} go={go} />}
          {drafts.length === 0 && <p className="muted small email-hint">Tag an email with the Outlook category 'Hub' to see it here.</p>}
          {!triage.length && !drafts.length && <Empty>Inbox zero.</Empty>}
          {triage.slice(0, 12).map((r) => (
            <div key={r.id} className="line" onClick={() => open_(r.id)}>
              <span className="line-title">{r.title}</span>
              <span className="line-meta">{r.requesterName} - {ageDays(r.createdAt)}d</span>
            </div>
          ))}
          {triage.length > 12 && <button className="linkish" onClick={() => go('/kanban')}>+{triage.length - 12} more on the Kanban</button>}
        </section>

        <section className="card">
          <div className="sec-head"><h2>Stuck over 7 days <span className="count">{stuck.length}</span></h2></div>
          {!stuck.length && <Empty>Nothing stuck.</Empty>}
          {stuck.slice(0, 12).map((r) => (
            <div key={r.id} className="line" onClick={() => open_(r.id)}>
              <span className="line-title">{r.title}</span>
              <span className="line-meta"><StatusTag status={r.status} /> {daysInStage(r)}d</span>
            </div>
          ))}
        </section>

        <section className="card">
          <div className="sec-head"><h2>Waiting on others <span className="count">{waiting.length}</span></h2></div>
          {!waiting.length && <Empty>Not waiting on anyone.</Empty>}
          {waiting.map((r) => (
            <div key={r.id} className="line" onClick={() => open_(r.id)}>
              <span className="line-title">{r.title}</span>
              <span className="line-meta">{r.waitingOn}</span>
            </div>
          ))}
        </section>
      </div>

      <section className="strip">
        <div><b>{doneWeek}</b> completed this week</div>
        <div><b>{releasedWeek}</b> released</div>
        <div><b>{plannedHours}h</b> planned of {settings.capacityWeek}h</div>
        <div><b>{open.length}</b> open</div>
      </section>
    </div>
  );
}

function Snapshot({ items, settings, go }: { items: Req[]; settings: Settings; go: (to: string) => void }) {
  const open = items.filter((r) => r.status !== 'completed');
  const hot = (r: Req) => ['critical', 'high'].includes(tier(score(r.impact, r.urgency), settings.tiers));
  const groupCount = (g: string) => open.filter((r) => STATUSES.find((s) => s.id === r.status)?.group === g).length;
  const doneMonth = items.filter((r) => r.status === 'completed' && within(r.completedAt, 30)).length;
  const robin = lineupOrder(open.filter((r) => /robin/i.test(r.requesterName)));
  const shipped = items.filter((r) => r.status === 'completed')
    .sort((a, b) => (b.completedAt?.toMillis() ?? 0) - (a.completedAt?.toMillis() ?? 0)).slice(0, 5);

  return (
    <div className="home">
      <section className="tiles">
        {DIVISIONS.map((d) => {
          const mine = open.filter((r) => r.divisions.includes(d.id));
          const urgent = mine.filter(hot).length;
          return (
            <button key={d.id} className="tile" onClick={() => go(`/kanban?division=${d.id}`)}>
              <span className="tile-label">{d.label}</span>
              <span className="tile-num">{mine.length}</span>
              <span className="tile-sub">{urgent ? `${urgent} urgent` : 'open'}</span>
            </button>
          );
        })}
      </section>

      <section className="card">
        <h2>Pipeline</h2>
        <div className="pipeline">
          {STAGE_GROUPS.filter((g) => g.id !== 'done').map((g, i) => (
            <div key={g.id} className="pipe-step">
              {i > 0 && <span className="pipe-arrow">&rsaquo;</span>}
              <span className="pipe-label">{g.label}</span>
              <b>{groupCount(g.id)}</b>
            </div>
          ))}
          <div className="pipe-step done"><span className="pipe-label">Completed (30d)</span><b>{doneMonth}</b></div>
        </div>
      </section>

      <div className="home-cols two">
        <section className="card">
          <div className="sec-head"><h2>Robin's asks <span className="count">{robin.length}</span></h2></div>
          {!robin.length && <Empty>None open.</Empty>}
          {robin.map((r) => (
            <div key={r.id} className="line" onClick={() => go(`/r/${r.id}`)}>
              <span className="line-title">{r.title}</span>
              <span className="line-meta"><TierBadge r={r} settings={settings} /> <StatusTag status={r.status} /></span>
            </div>
          ))}
        </section>
        <section className="card">
          <div className="sec-head"><h2>Recently shipped</h2></div>
          {!shipped.length && <Empty>Nothing completed yet.</Empty>}
          {shipped.map((r) => (
            <div key={r.id} className="line" onClick={() => go(`/r/${r.id}`)}>
              <span className="line-title">{r.title}</span>
              <span className="line-meta">{r.releaseNote ? r.releaseNote.slice(0, 80) : r.completedAt?.toDate().toLocaleDateString()}</span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

/** Email drafts (spec page 2b): accept -> a New request with the thread link; dismiss -> gone for good. */
function EmailDrafts({ drafts, go }: { drafts: EmailDraft[]; go: (to: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const act = async (id: string, fn: () => Promise<void>) => {
    setBusy(id); setErr(null);
    try { await fn(); } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };
  return (
    <div className="drafts">
      <div className="drafts-head">From your email <span className="count">{drafts.length}</span></div>
      {err && <p className="err">{err}</p>}
      {drafts.slice(0, 8).map((m) => (
        <div key={m.id} className="draft">
          <div className="draft-main">
            <a className="line-title" href={m.webLink} target="_blank" rel="noreferrer">{m.subject || '(no subject)'}</a>
            <div className="line-meta">{m.fromName || m.from} - {m.receivedAt ? ageDays(m.receivedAt) + 'd' : ''}</div>
          </div>
          <button disabled={busy === m.id} onClick={() => act(m.id, async () => { const id = await acceptEmail(m.id); go(`/r/${id}`); })}>Accept</button>
          <button className="secondary" disabled={busy === m.id} onClick={() => act(m.id, () => dismissEmail(m.id))}>Dismiss</button>
        </div>
      ))}
      {drafts.length > 8 && <div className="muted small">+{drafts.length - 8} more</div>}
    </div>
  );
}
